import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts'
import { Plus, TrendingUp, TrendingDown, Minus, Settings, Edit3, Trash2 } from 'lucide-react'
import { weightAPI } from '../../services/api'
import toast from 'react-hot-toast'

interface WeightTrackerWidgetProps {
  widget: any
  isExpanded: boolean
}

interface WeightEntry {
  id: number
  weight: number
  unit: string
  entry_date: string
  notes?: string
  created_at: string
}

export default function WeightTrackerWidget({ widget, isExpanded }: WeightTrackerWidgetProps) {
  const [showAddEntry, setShowAddEntry] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  // Load preferred unit from localStorage, default to 'lbs'
  const [preferredUnit, setPreferredUnit] = useState(() => {
    try {
      const saved = localStorage.getItem(`weight-tracker-unit-${widget.id}`)
      return saved || 'lbs'
    } catch {
      return 'lbs'
    }
  })

  const [newEntry, setNewEntry] = useState({
    weight: '',
    unit: preferredUnit,
    entry_date: new Date().toISOString().split('T')[0],
    notes: ''
  })
  const [editingEntry, setEditingEntry] = useState<WeightEntry | null>(null)
  const queryClient = useQueryClient()

  // Save preferred unit to localStorage
  const savePreferredUnit = (unit: string) => {
    setPreferredUnit(unit)
    try {
      localStorage.setItem(`weight-tracker-unit-${widget.id}`, unit)
    } catch (error) {
      console.error('Failed to save preferred unit:', error)
    }
  }

  // Fetch weight entries
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['weight-entries'],
    queryFn: async () => {
      const { data } = await weightAPI.getEntries()
      return data as WeightEntry[]
    },
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['weight-stats'],
    queryFn: async () => {
      const { data } = await weightAPI.getStats()
      return data
    },
  })

  // Prepare chart data with better formatting
  const chartData = entries
    .sort((a, b) => new Date(a.entry_date).getTime() - new Date(b.entry_date).getTime())
    .map((entry, index) => ({
      date: new Date(entry.entry_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: entries.length > 30 ? '2-digit' : undefined // Show year if data spans multiple years
      }),
      fullDate: new Date(entry.entry_date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      weight: entry.weight,
      unit: entry.unit,
      index: index,
      dateObj: new Date(entry.entry_date)
    }))

  // Calculate trend line (moving average)
  const trendData = chartData.map((point, index) => {
    const windowSize = Math.min(7, chartData.length) // 7-day moving average
    const start = Math.max(0, index - Math.floor(windowSize / 2))
    const end = Math.min(chartData.length, start + windowSize)
    const sum = chartData.slice(start, end).reduce((acc, p) => acc + p.weight, 0)
    return {
      ...point,
      trend: sum / (end - start)
    }
  })

  const handleSaveEntry = async () => {
    try {
      // Save the unit preference when saving an entry
      savePreferredUnit(newEntry.unit)

      if (editingEntry) {
        // Update existing entry by ID
        await weightAPI.updateEntry(editingEntry.id, {
          weight: parseFloat(newEntry.weight),
          unit: newEntry.unit,
          entry_date: newEntry.entry_date,
          notes: newEntry.notes
        })
        setEditingEntry(null)
        toast.success('Weight entry updated!')
      } else {
        // Create new entry
        await weightAPI.saveEntry({
          weight: parseFloat(newEntry.weight),
          unit: newEntry.unit,
          entry_date: newEntry.entry_date,
          notes: newEntry.notes
        })
        toast.success('Weight entry added!')
      }

      queryClient.invalidateQueries({ queryKey: ['weight-entries'] })
      queryClient.invalidateQueries({ queryKey: ['weight-stats'] })
      setShowAddEntry(false)
      setNewEntry({
        weight: '',
        unit: preferredUnit,
        entry_date: new Date().toISOString().split('T')[0],
        notes: ''
      })
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save entry')
    }
  }

  const handleEditEntry = (entry: WeightEntry) => {
    setEditingEntry(entry)
    setNewEntry({
      weight: entry.weight.toString(),
      unit: entry.unit, // Keep the original entry's unit when editing
      entry_date: entry.entry_date,
      notes: entry.notes || ''
    })
    setShowAddEntry(true)
  }

  const handleDeleteEntry = async (entryId: number) => {
    if (!confirm('Are you sure you want to delete this entry?')) return

    try {
      await weightAPI.deleteEntry(entryId)
      queryClient.invalidateQueries({ queryKey: ['weight-entries'] })
      queryClient.invalidateQueries({ queryKey: ['weight-stats'] })
      toast.success('Weight entry deleted!')
    } catch (error) {
      toast.error('Failed to delete entry')
    }
  }

  const getWeightChangeIcon = () => {
    if (!stats || typeof stats.weight_change_30_days !== 'number') return <Minus size={16} className="text-gray-500" />

    return stats.weight_change_30_days > 0 ?
      <TrendingUp size={16} className="text-red-500" /> :
      <TrendingDown size={16} className="text-green-500" />
  }

  const getWeightChangeColor = () => {
    if (!stats || typeof stats.weight_change_30_days !== 'number') return 'text-gray-500'
    return stats.weight_change_30_days > 0 ? 'text-red-500' : 'text-green-500'
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-gray-200 space-y-2">
        {/* Header with current stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>Weight Tracker</h3>
            {stats?.latest && (
              <div className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}>
                {stats.latest.weight} {stats.latest.unit}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowAddEntry(true)}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="Add weight entry"
            >
              <Plus size={14} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="Settings"
            >
              <Settings size={14} />
            </button>
          </div>
        </div>

        {/* Weight change indicator */}
        {stats && typeof stats.weight_change_30_days === 'number' && (
          <div className="flex items-center gap-1 text-xs">
            {getWeightChangeIcon()}
            <span className={getWeightChangeColor()}>
              {stats.weight_change_30_days !== null ? (stats.weight_change_30_days > 0 ? '+' : '') + stats.weight_change_30_days.toFixed(1) : 'N/A'} {stats.latest?.unit} (30d)
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        {isExpanded ? (
          <div className="h-full p-3 space-y-4">
            {/* Modern Chart */}
            <div className="h-64 relative">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.4}/>
                      <stop offset="50%" stopColor="var(--color-primary)" stopOpacity={0.2}/>
                      <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff6b6b" stopOpacity={0.6}/>
                      <stop offset="100%" stopColor="#ff6b6b" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="2 2"
                    stroke="var(--color-border)"
                    opacity={0.3}
                  />

                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                    interval="preserveStartEnd"
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                    domain={['dataMin - 0.5', 'dataMax + 0.5']}
                    width={35}
                  />

                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {data.fullDate}
                            </p>
                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                              {payload[0].value} {data.unit}
                            </p>
                            {payload[1] && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Trend: {payload[1].value ? payload[1].value.toFixed(1) : 'N/A'} {data.unit}
                              </p>
                            )}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />

                  {/* Trend line (background) */}
                  <Area
                    type="monotone"
                    dataKey="trend"
                    stroke="#ff6b6b"
                    fill="url(#trendGradient)"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                  />

                  {/* Main weight area */}
                  <Area
                    type="monotone"
                    dataKey="weight"
                    stroke="var(--color-primary)"
                    fill="url(#weightGradient)"
                    strokeWidth={3}
                    dot={{ r: 4, fill: 'var(--color-primary)', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: 'var(--color-primary)', strokeWidth: 2, stroke: '#fff' }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Chart Legend */}
              <div className="absolute top-2 right-2 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Weight</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-0.5 bg-red-400 opacity-60" style={{ borderStyle: 'dashed' }}></div>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Trend</span>
                </div>
              </div>
            </div>

            {/* Recent entries */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>Recent Entries</h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {entries.slice(0, 10).map((entry: WeightEntry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-2 rounded text-sm"
                    style={{ backgroundColor: 'var(--color-surface)' }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {entry.weight} {entry.unit}
                      </span>
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditEntry(entry)}
                        className="p-1 hover:bg-gray-200 rounded transition"
                        title="Edit entry"
                      >
                        <Edit3 size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="p-1 hover:bg-red-100 text-red-600 rounded transition"
                        title="Delete entry"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Compact view - modern mini chart */
          <div className="h-full p-2 relative">
            {chartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData.slice(-10)} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="compactWeightGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.6}/>
                        <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="weight"
                      stroke="var(--color-primary)"
                      fill="url(#compactWeightGradient)"
                      strokeWidth={2}
                      dot={{ r: 2, fill: 'var(--color-primary)', strokeWidth: 1, stroke: '#fff' }}
                      activeDot={{ r: 3, fill: 'var(--color-primary)', strokeWidth: 2, stroke: '#fff' }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-xs shadow border border-gray-200 dark:border-gray-700">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {data.fullDate}
                              </div>
                              <div className="font-bold text-blue-600 dark:text-blue-400">
                                {payload[0].value} {data.unit}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>

                {/* Latest weight display */}
                <div className="absolute bottom-1 left-1 text-xs">
                  <div className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    {entries[entries.length - 1]?.weight} {entries[entries.length - 1]?.unit}
                  </div>
                  <div style={{ color: 'var(--color-text-secondary)' }}>
                    {new Date(entries[entries.length - 1]?.entry_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <div className="text-2xl mb-2">⚖️</div>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    No weight entries yet
                  </p>
                  <button
                    onClick={() => setShowAddEntry(true)}
                    className="text-xs mt-2 px-2 py-1 rounded transition font-medium"
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      color: 'white'
                    }}
                  >
                    Add First Entry
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Entry Modal */}
      {showAddEntry && (
        <div className="absolute inset-0 rounded-lg p-4 flex flex-col z-20" style={{ backgroundColor: 'var(--color-widget-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {editingEntry ? 'Edit Weight Entry' : 'Add Weight Entry'}
            </h3>
            <button onClick={() => {
              setShowAddEntry(false)
              setEditingEntry(null)
              setNewEntry({
                weight: '',
                unit: preferredUnit,
                entry_date: new Date().toISOString().split('T')[0],
                notes: ''
              })
            }} className="p-1 hover:bg-gray-100 rounded">
              ×
            </button>
          </div>

          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Weight
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newEntry.weight}
                  onChange={(e) => setNewEntry({...newEntry, weight: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                  placeholder="70.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Unit
                </label>
                <select
                  value={newEntry.unit}
                  onChange={(e) => setNewEntry({...newEntry, unit: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <option value="kg">kg</option>
                  <option value="lbs">lbs</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Date
              </label>
              <input
                type="date"
                value={newEntry.entry_date}
                onChange={(e) => setNewEntry({...newEntry, entry_date: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Notes (optional)
              </label>
              <textarea
                value={newEntry.notes}
                onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
                rows={3}
                placeholder="How do you feel? Any notes about your progress..."
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSaveEntry}
              disabled={!newEntry.weight}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {editingEntry ? 'Update Entry' : 'Save Entry'}
            </button>
            <button
              onClick={() => {
                setShowAddEntry(false)
                setEditingEntry(null)
                setNewEntry({
                  weight: '',
                  unit: preferredUnit,
                  entry_date: new Date().toISOString().split('T')[0],
                  notes: ''
                })
              }}
              className="px-4 py-2 rounded-lg transition"
              style={{
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="absolute inset-0 rounded-lg p-4 flex flex-col z-20" style={{ backgroundColor: 'var(--color-widget-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Weight Tracker Settings</h3>
            <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-gray-100 rounded">
              ×
            </button>
          </div>

          <div className="flex-1 space-y-4">
            <div>
              <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Unit Preference</h4>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="preferredUnit"
                    value="lbs"
                    checked={preferredUnit === 'lbs'}
                    onChange={(e) => savePreferredUnit(e.target.value)}
                    className="mr-2"
                  />
                  <span style={{ color: 'var(--color-text-primary)' }}>Pounds (lbs)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="preferredUnit"
                    value="kg"
                    checked={preferredUnit === 'kg'}
                    onChange={(e) => savePreferredUnit(e.target.value)}
                    className="mr-2"
                  />
                  <span style={{ color: 'var(--color-text-primary)' }}>Kilograms (kg)</span>
                </label>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Statistics</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Total Entries</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stats?.stats?.total_entries || 0}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Current Weight</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {stats?.latest ? `${stats.latest.weight} ${stats.latest.unit}` : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Starting Weight</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {stats?.stats?.first_entry ? new Date(stats.stats.first_entry).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Weight Range</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {stats?.stats?.min_weight && stats?.stats?.max_weight ?
                      `${stats.stats.min_weight} - ${stats.stats.max_weight} ${stats?.latest?.unit || 'kg'}` :
                      'N/A'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowSettings(false)}
            className="mt-4 w-full px-4 py-2 rounded-lg hover:bg-gray-300 transition"
            style={{
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)'
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  )
}
